import { useState, useCallback, useMemo } from 'react'
import type { LeadQualityData, CallRecord } from './types'
import { fetchCalls, getExportUrl } from './api'
import { KpiCardsRow } from './components/KpiCardsRow'
import { GclidFunnel } from './components/GclidFunnel'
import { QualityDonutChart } from './components/QualityDonutChart'
import { IrrelevantReasonsChart } from './components/IrrelevantReasonsChart'
import { CallFilters } from './components/CallFilters'
import { CallLogTable } from './components/CallLogTable'

interface LeadQualityDashboardProps {
  initialData: LeadQualityData
}

export function LeadQualityDashboard({ initialData }: LeadQualityDashboardProps) {
  const [data, setData] = useState(initialData)
  const [calls, setCalls] = useState<CallRecord[]>(initialData.calls)
  const [pagination, setPagination] = useState(initialData.pagination)
  const [dateFilter, setDateFilter] = useState(initialData.filters.date_filter)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Debounce search
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)

  const handleDateFilterChange = useCallback((value: string) => {
    setDateFilter(value)
    // Reload page with new date filter
    window.location.href = `/dashboard/lead-quality?date_filter=${value}`
  }, [])

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)

    // Debounce API call
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }

    const timeout = setTimeout(async () => {
      setIsLoading(true)
      try {
        const response = await fetchCalls({
          date_filter: dateFilter,
          page: 1,
          search: value,
        })
        setCalls(response.calls)
        setPagination(response.pagination)
      } catch (error) {
        console.error('Failed to fetch calls:', error)
      } finally {
        setIsLoading(false)
      }
    }, 300)

    setSearchTimeout(timeout)
  }, [dateFilter, searchTimeout])

  const handlePageChange = useCallback(async (page: number) => {
    setIsLoading(true)
    try {
      const response = await fetchCalls({
        date_filter: dateFilter,
        page,
        search,
      })
      setCalls(response.calls)
      setPagination(response.pagination)
    } catch (error) {
      console.error('Failed to fetch calls:', error)
    } finally {
      setIsLoading(false)
    }
  }, [dateFilter, search])

  const handleExport = useCallback(() => {
    const url = getExportUrl({ date_filter: dateFilter })
    window.open(url, '_blank')
  }, [dateFilter])

  const handlePlayRecording = useCallback((resourceId: string) => {
    // Open recording in new window or use audio player
    // For now, we'll just log it - can integrate with existing AudioPlayer
    console.log('Play recording:', resourceId)
    // TODO: Integrate with AudioPlayer component
  }, [])

  // Filter calls client-side for immediate feedback while API loads
  const filteredCalls = useMemo(() => {
    if (!search) return calls

    const searchLower = search.toLowerCase()
    return calls.filter((call) =>
      call.customer_name?.toLowerCase().includes(searchLower) ||
      call.customer_phone?.includes(search)
    )
  }, [calls, search])

  return (
    <div className="space-y-6">
      {/* KPI Cards Row */}
      <KpiCardsRow stats={data.stats} />

      {/* Charts Row */}
      <div className="grid grid-cols-3 gap-4">
        <GclidFunnel funnel={data.stats.funnel} />
        <QualityDonutChart
          breakdown={data.stats.quality_breakdown}
          totalPaidCalls={data.stats.paid_calls}
          totalCalls={data.stats.total_calls}
        />
        <IrrelevantReasonsChart breakdown={data.stats.irrelevant_breakdown} />
      </div>

      {/* Call Log Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Detailed Call Log</h2>
        <CallFilters
          dateFilter={dateFilter}
          dateFilters={data.date_filters}
          search={search}
          onDateFilterChange={handleDateFilterChange}
          onSearchChange={handleSearchChange}
          onExport={handleExport}
        />
        <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
          <CallLogTable
            calls={filteredCalls}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPlayRecording={handlePlayRecording}
          />
        </div>
      </div>
    </div>
  )
}

export default LeadQualityDashboard
